const API_BASE_URL = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

export interface OAuthUser {
  id: string
  email: string
  full_name: string
  role: 'customer' | 'broker' | 'admin' | 'data_operator'
  is_new_user?: boolean
}

export interface OAuthTokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  user: OAuthUser
}

const AUTH_KEYS = {
  ACCESS: 'loanloop_access_token',
  REFRESH: 'loanloop_refresh_token',
  USER: 'loanloop_user',
}

export const authApi = {
  getApiUrl(): string {
    return API_BASE_URL
  },

  async getGoogleAuthUrl(redirectUri: string): Promise<string> {
    const res = await fetch(
      `${API_BASE_URL}/api/auth/oauth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}&state=google`
    )
    if (!res.ok) throw new Error('Failed to get Google auth URL')
    const data = await res.json()
    return data.url
  },

  async getAppleAuthUrl(frontendCallback: string): Promise<string> {
    const res = await fetch(
      `${API_BASE_URL}/api/auth/oauth/apple/url?frontend_callback=${encodeURIComponent(frontendCallback)}&state=apple`
    )
    if (!res.ok) throw new Error('Failed to get Apple auth URL')
    const data = await res.json()
    return data.url
  },

  async getMicrosoftAuthUrl(redirectUri: string, codeChallenge?: string): Promise<string> {
    const params = new URLSearchParams({
      redirect_uri: redirectUri,
      state: 'microsoft',
    })
    if (codeChallenge) {
      params.set('code_challenge', codeChallenge)
      params.set('code_challenge_method', 'S256')
    }
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/microsoft/url?${params}`)
    if (!res.ok) throw new Error('Failed to get Microsoft auth URL')
    const data = await res.json()
    return data.url
  },

  async exchangeGoogleCode(code: string, redirectUri: string, role?: 'customer' | 'broker'): Promise<OAuthTokenResponse> {
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri, role }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Google sign-in failed')
    }
    return res.json()
  },

  async exchangeAppleCode(code: string, _redirectUri?: string): Promise<OAuthTokenResponse> {
    // redirect_uri must match what Apple used during authorization.
    // The backend's /oauth/apple/url endpoint uses APPLE_REDIRECT_URI env var,
    // which points to API_BASE_URL/api/auth/oauth/apple/redirect
    const appleRedirect =
      import.meta.env.VITE_APPLE_REDIRECT_URI ||
      `${API_BASE_URL}/api/auth/oauth/apple/redirect`
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/apple`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: appleRedirect }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Apple sign-in failed')
    }
    return res.json()
  },

  async exchangeMicrosoftCode(
    code: string,
    redirectUri: string,
    role?: 'customer' | 'broker',
    codeVerifier?: string
  ): Promise<OAuthTokenResponse> {
    const body: Record<string, string> = { code, redirect_uri: redirectUri }
    if (role) body.role = role
    if (codeVerifier) body.code_verifier = codeVerifier
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/microsoft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Microsoft sign-in failed')
    }
    return res.json()
  },

  async getFacebookAuthUrl(redirectUri: string): Promise<string> {
    const res = await fetch(
      `${API_BASE_URL}/api/auth/oauth/facebook/url?redirect_uri=${encodeURIComponent(redirectUri)}&state=facebook`
    )
    if (!res.ok) throw new Error('Failed to get Facebook auth URL')
    const data = await res.json()
    return data.url
  },

  async exchangeFacebookCode(code: string, redirectUri: string): Promise<OAuthTokenResponse> {
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/facebook`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Facebook sign-in failed')
    }
    return res.json()
  },

  async refreshToken(refreshToken: string): Promise<OAuthTokenResponse> {
    const res = await fetch(`${API_BASE_URL}/api/auth/oauth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) throw new Error('Session expired')
    return res.json()
  },

  async setRole(role: 'customer' | 'broker', accessToken: string): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/api/auth/users/me/role`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ role }),
    })
    if (!res.ok) throw new Error('Failed to set role')
  },

  saveTokens(data: Pick<OAuthTokenResponse, 'access_token' | 'user'> & { refresh_token?: string }): void {
    localStorage.setItem(AUTH_KEYS.ACCESS, data.access_token)
    localStorage.setItem(AUTH_KEYS.REFRESH, data.refresh_token ?? '')
    localStorage.setItem(AUTH_KEYS.USER, JSON.stringify(data.user))
  },

  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_KEYS.ACCESS)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_KEYS.REFRESH)
  },

  getUser(): OAuthUser | null {
    const u = localStorage.getItem(AUTH_KEYS.USER)
    return u ? JSON.parse(u) : null
  },

  clearTokens(): void {
    localStorage.removeItem(AUTH_KEYS.ACCESS)
    localStorage.removeItem(AUTH_KEYS.REFRESH)
    localStorage.removeItem(AUTH_KEYS.USER)
    localStorage.removeItem('loanloop_user') // legacy
  },
}

// ── Authenticated Fetch with Token Refresh ──────────────────────────────────
// Wraps fetch() to automatically handle 401 responses by attempting token
// refresh before giving up. Prevents infinite loops with a retry flag.

let isRefreshing = false
let refreshPromise: Promise<OAuthTokenResponse | null> | null = null

async function tryRefresh(): Promise<OAuthTokenResponse | null> {
  const refreshToken = authApi.getRefreshToken()
  if (!refreshToken) return null
  try {
    const data = await authApi.refreshToken(refreshToken)
    authApi.saveTokens(data)
    return data
  } catch {
    authApi.clearTokens()
    return null
  }
}

/**
 * Drop-in replacement for fetch() that handles expired tokens.
 * On 401: attempts refresh, retries with new token, or dispatches session-expired.
 * Caller still provides the Authorization header; this wrapper replaces it on retry.
 */
export async function authenticatedFetch(url: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(url, options)
  if (res.status !== 401) return res

  // Deduplicate concurrent refresh attempts
  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = tryRefresh().finally(() => {
      isRefreshing = false
      refreshPromise = null
    })
  }

  const refreshData = await (refreshPromise ?? tryRefresh())
  if (!refreshData) {
    window.dispatchEvent(new Event('session-expired'))
    return res
  }

  // Retry original request with the new access token
  const newHeaders = new Headers(options?.headers)
  newHeaders.set('Authorization', `Bearer ${refreshData.access_token}`)
  return fetch(url, { ...options, headers: newHeaders })
}
